// Local stub for static-checklist state plus per-tab UI navigation state.
// The static-checklist half (seeded from a small illustrative template per
// checklistKey) is no longer the checklist tab's own data source — the derived
// Checklist tab reads the real `work_order_checklist` instance via
// `useChecklistInstance` — but `assembleGuardContext` still reads it into
// `GuardContext.checklists` for the `checklist_complete` guard evaluator.
// Change requests are NOT here: they are persisted server-side (`useChangeRequests`).
// Resets on reload.
//
// Item LABELS below are this stub's own illustrative content, not confirmed
// against any real design source — only the checklistKey names
// (previo/production/distribution) and the "every required item + separate
// confirmation" completion rule are.

import type { ChecklistItem, ChecklistState, ValidationLane } from '@/domain/workflow.types'

type Lane = ValidationLane

const STATIC_TEMPLATES: Readonly<Record<string, ReadonlyArray<Omit<ChecklistItem, 'checked'>>>> = {
	previo: [
		{ itemId: 'previo-1', label: 'Brief reviewed and understood', required: true },
		{ itemId: 'previo-2', label: 'Brand guidelines confirmed for this asset type', required: true },
	],
	production: [
		{ itemId: 'production-1', label: 'Copy approved by messaging owner', required: true },
		{ itemId: 'production-2', label: 'Final visual signed off', required: true },
		{ itemId: 'production-3', label: 'Alt-text drafted', required: false },
	],
	distribution: [
		{ itemId: 'distribution-1', label: 'Package contents match the manifest', required: true },
		{ itemId: 'distribution-2', label: 'File naming convention checked', required: true },
	],
}

/**
 * Human label per checklistKey — the keys themselves ("previo" is the real
 * ToBe mockup's own internal name for this one) are internal identifiers, not
 * copy; anywhere user-facing (gate modal title, "what's missing" messaging)
 * must go through this, never render the raw key.
 */
const CHECKLIST_LABELS: Readonly<Record<string, string>> = {
	previo: 'Pre-production checklist',
	production: 'Production checklist',
	distribution: 'Distribution checklist',
}

export function checklistLabel(key: string): string {
	return CHECKLIST_LABELS[key] ?? `"${key}" checklist`
}

const checklists = new Map<string, ChecklistState>()
const generatedArtifacts = new Map<string, Set<string>>()
const laneScreens = new Map<string, LaneScreen>()
const activeWorkspaceTabs = new Map<string, string>()
const activeWorkspaceTabKey = (workOrderId: string, status: string) => `${workOrderId}|${status}`

export type LaneScreen = 'review' | 'pm'

const listeners = new Set<() => void>()
let version = 0
/** So `useChecklistStub` can re-render on mutation — a plain module store has no React subscription of its own. */
export function subscribeChecklistStub(listener: () => void): () => void {
	listeners.add(listener)
	return () => listeners.delete(listener)
}
/** `useSyncExternalStore`'s snapshot — a bumped counter, not the store itself, since the store's own Maps are mutated in place. */
export function getChecklistStubVersion(): number {
	return version
}
function notify(): void {
	version++
	for (const listener of listeners) listener()
}

const checklistKey = (workOrderId: string, key: string) => `${workOrderId}:${key}`
const laneScreenKey = (workOrderId: string, lane: Lane) => `${workOrderId}:${lane}`

/** Lazily seeds from the static template on first read — no explicit "create" step needed. */
export function getChecklistState(workOrderId: string, key: string): ChecklistState {
	const existing = checklists.get(checklistKey(workOrderId, key))
	if (existing) return existing
	const template = STATIC_TEMPLATES[key] ?? []
	const seeded: ChecklistState = {
		mode: 'static',
		items: template.map((item) => ({ ...item, checked: false })),
		confirmed: false,
	}
	checklists.set(checklistKey(workOrderId, key), seeded)
	return seeded
}

/**
 * Checking/unchecking a REQUIRED item clears `confirmed` — the separate
 * confirmation must be re-affirmed. A non-required item never gates
 * completion, so toggling one leaves an existing confirmation untouched —
 * otherwise ticking an optional item after confirming would silently
 * re-disable "Close step & continue", which is exactly the item's own
 * "optional" tag contradicting itself.
 */
export function setChecklistItemChecked(workOrderId: string, key: string, itemId: string, checked: boolean): void {
	const state = getChecklistState(workOrderId, key)
	const item = state.items.find((i) => i.itemId === itemId)
	checklists.set(checklistKey(workOrderId, key), {
		...state,
		items: state.items.map((i) => (i.itemId === itemId ? { ...i, checked } : i)),
		confirmed: item?.required ? false : state.confirmed,
	})
	notify()
}

/** The gate modal's separate "I confirm I reviewed and completed every point" checkbox — a no-op if a required item is still unchecked. */
export function confirmChecklist(workOrderId: string, key: string): void {
	const state = getChecklistState(workOrderId, key)
	if (!state.items.filter((item) => item.required).every((item) => item.checked)) return
	checklists.set(checklistKey(workOrderId, key), { ...state, confirmed: true })
	notify()
}

/** The symmetric un-check, in case the confirmer changes their mind before submitting. */
export function unconfirmChecklist(workOrderId: string, key: string): void {
	const state = getChecklistState(workOrderId, key)
	checklists.set(checklistKey(workOrderId, key), { ...state, confirmed: false })
	notify()
}

/** Flipped by a real user action (e.g. downloading the distribution package) — never defaulted true. */
export function markArtifactGenerated(workOrderId: string, artifact: string): void {
	const set = generatedArtifacts.get(workOrderId) ?? new Set<string>()
	set.add(artifact)
	generatedArtifacts.set(workOrderId, set)
	notify()
}

export function hasArtifactGenerated(workOrderId: string, artifact: string): boolean {
	return generatedArtifacts.get(workOrderId)?.has(artifact) ?? false
}

/**
 * Which of a validation lane's two screens (review, then PM) is showing — pure
 * ephemeral navigation state, confirmed against the real AsIs mockup's
 * `wsLaneScreen`; never read by a guard. Defaults to `review` — the first
 * screen every lane starts on.
 */
export function getLaneScreen(workOrderId: string, lane: Lane): LaneScreen {
	return laneScreens.get(laneScreenKey(workOrderId, lane)) ?? 'review'
}

/**
 * Set when the reviewer moves on to PM validation, or reset to `review` when a
 * transition lands the order on a validation lane — a fresh gate every entry,
 * the same "no stale state from a previous visit" rule already applied to
 * static checklists.
 */
export function setLaneScreen(workOrderId: string, lane: Lane, screen: LaneScreen): void {
	laneScreens.set(laneScreenKey(workOrderId, lane), screen)
	notify()
}

/**
 * Which workspace tab (authored or derived) is showing — cross-cutting UI
 * navigation state, not a checklist concern itself, but the action bar's
 * locked/checklist-gated CTA is its only writer today: clicking it selects
 * the derived "checklist" tab instead of opening a modal, so completing the
 * checklist happens inline, in whichever workspace is currently mounted.
 * `undefined` (no entry) means "let `WorkspaceTabs` fall back to its own
 * default (`tabs[0]`)" — never defaulted to a guessed key here.
 *
 * Keyed by `(workOrderId, status)`, not `workOrderId` alone: a transition
 * lands the work order on a new status, which `WorkspaceCatalog` renders via
 * a freshly-mounted workspace component (keyed on the resolved workspace
 * code), so a plain `workOrderId` key would keep surfacing the outgoing
 * status's tab (e.g. its Checklist pill) as soon as the new one mounts. A
 * status the store has never seen for this work order has no entry, so it
 * falls back to `tabs[0]` for free — no explicit reset needed.
 */
export function getActiveWorkspaceTab(workOrderId: string, status: string): string | undefined {
	return activeWorkspaceTabs.get(activeWorkspaceTabKey(workOrderId, status))
}

export function setActiveWorkspaceTab(workOrderId: string, status: string, key: string): void {
	activeWorkspaceTabs.set(activeWorkspaceTabKey(workOrderId, status), key)
	notify()
}

/** Test-only: clears every in-memory entry so specs don't leak state across cases. */
export function resetChecklistStub(): void {
	checklists.clear()
	generatedArtifacts.clear()
	laneScreens.clear()
	activeWorkspaceTabs.clear()
}
